import React,{useState,useEffect} from 'react';
import { Download, Clock, CheckCircle, MoveUpLeft, Radio, ChevronDown, ChevronUp, Lock, Play, BookOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../services/axiosInstance';
import DownloadNurseCertifcate from '../nurse/DownloadNurseCertficate';
import CourseInstruction from '../nurse/CourseInstruction';
import CourseCard from './CourseCard'
import DOMPurify from 'dompurify';


const CourseOverviewLoader = () => (
  <div className="bg-white min-h-screen animate-pulse">
    <div className="mx-auto px-4 py-8">

      {/* TODO: Header section */}
      <div className="mb-8">
        <div className="h-7 w-2/3 bg-gray-200 rounded" />
        <div className="mt-3 h-4 w-5/6 bg-gray-200 rounded" />
      </div>


      {/* TODO: Progress row: left card + right circular progress */}
      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 items-start justify-between mt-6">
        {/* Left: Progress card */}
        <div className="bg-white rounded-lg shadow-md p-4 lg:p-6 border border-gray-100 flex-1 w-full">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <div className="h-6 w-24 bg-gray-200 rounded-full" />
              <div className="h-4 w-48 bg-gray-200 rounded" />
            </div>
            <div className="w-full">
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div className="bg-gray-300 h-3 rounded-full w-1/3" />
              </div>
            </div>
          </div>
        </div>

        {/* Right: Circular progress */}
        <div className="text-center lg:ml-auto w-full lg:w-auto">
          <div className="relative inline-block">
            <div className="w-24 h-24 lg:w-28 lg:h-28 rounded-full bg-gray-200" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-6 w-12 bg-gray-200 rounded" />
            </div>
          </div>
          <div className="mt-2 h-4 w-56 bg-gray-200 rounded mx-auto" />
        </div>
      </div>

      {/* TODO: Modules grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="bg-white rounded-lg shadow-md p-6 border border-gray-100"
          >
            {/* Card header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="h-4 w-20 bg-gray-200 rounded" />
                <div className="h-4 w-4 bg-gray-200 rounded-full" />
              </div>
              <div className="h-5 w-5 bg-gray-200 rounded" />
            </div>

            {/* Title */}
            <div className="h-5 w-4/5 bg-gray-200 rounded mb-3" />

            {/* Meta row */}
            <div className="flex items-center gap-2 text-sm mb-3">
              <div className="w-4 h-4 bg-gray-300 rounded" />
              <div className="h-4 w-24 bg-gray-200 rounded" />
            </div>
          </div>
        ))}
      </div>

      {/* TODO: Certificate section placeholder */}
      <div className="text-center mt-12 py-8 bg-gray-50 rounded-lg">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-200 rounded-full mb-6" />
        <div className="h-6 w-3/4 bg-gray-200 rounded mx-auto mb-3" />
        <div className="h-4 w-2/3 bg-gray-200 rounded mx-auto" />
        <div className="h-10 w-44 bg-gray-300 rounded-lg mx-auto mt-4" />
      </div>

    </div>

  </div>
);

function ADHDUserCourse() {
  const [modules, setModules] = useState([])
  const [courseinfo, setCourseinfo] = useState({title:"", overview:"", description:""})
  const [isloading, setIsloading] = useState(true)
  const [expandedModules, setExpandedModules] = useState(new Set())
  const navigate = useNavigate();

  
  useEffect(() => {
  const fetchCourse = async () => {
    try {
      const res = await axiosInstance.get('/course');
      const {coursemodules,courseinfo} = res.data.data
      setCourseinfo(courseinfo)
      setModules(coursemodules);
      setIsloading(false)
    } catch (error) {
      console.error("Error fetching course:", error);
    }
    };
    fetchCourse();
  }, []);

  const toggleModuleExpansion = (moduleId) => {
    const newExpanded = new Set(expandedModules);
    if (newExpanded.has(moduleId)) {
      newExpanded.delete(moduleId);
    } else {
      newExpanded.add(moduleId);
    }
    setExpandedModules(newExpanded);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500';
      case 'live':
        return 'bg-orange-500';
      case 'locked':
        return 'bg-gray-400';
      case 'available':
        return 'bg-blue-500';
      default:
        return 'bg-gray-400';
    }
  };

  const getStatusBackgroundColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-50';
      case 'live':
        return 'bg-orange-50';
      case 'locked':
        return 'bg-gray-50';
      case 'available':
        return 'bg-blue-50';
      default:
        return 'bg-gray-50';
    }
  };

  const getStatusBorderColor = (status) => {
    switch (status) {
      case 'completed':
        return 'border-green-200';
      case 'live':
        return 'border-orange-200';
      case 'locked':
        return 'border-gray-200';
      case 'available':
        return 'border-blue-200';
      default:
        return 'border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'completed') {
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    }
    if (status === 'live') {
      return <Radio className="w-4 h-4 text-red-600 animate-pulse" />
    }
    if (status === 'locked') {
      return <Lock className="w-4 h-4 text-gray-400" />;
    }
    if (status === 'available') {
    
      return <BookOpen className="w-4 h-4" />;
    }
    return null;
  };

  const handleModuleClick = async (moduleId) => {
    try {
      // POST request to start the module
      await axiosInstance.post(`/course/start-module/${moduleId}`);

      const fetchCourse = async () => {
        try {
          const res = await axiosInstance.get('/course');
          const {coursemodules,courseinfo} = res.data.data
          setCourseinfo(courseinfo)
          setModules(coursemodules);
          setIsloading(false)
        } catch (error) {
          console.error("Error fetching course:", error);
        }
        };
        fetchCourse();
    } catch (error) {
      console.error("Error starting module:", error);
    }
  };

  const handleStartQuiz = (moduleId) => {
    navigate(`/nurse/assement/${moduleId}`);
  };


  const completedModules = modules.filter(module => module.status === 'completed').length;
  const totalModules = modules.length;
  const progressPercentage = Math.round((completedModules / totalModules) * 100);

  return isloading ? (<CourseOverviewLoader/>): 
  (
    <>
    <div className="bg-white">
       <div className="mb-8 px-3">        
          {/* Progress Section with Card and Circular Progress */}
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 items-start justify-between mt-6">
            {/* Progress Card with Circular Progress Inside */}
            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100 flex-1 w-full">
              <div className="flex flex-col lg:flex-row gap-6 items-start">
                {/* Left Side - Progress Info */}
                <div className="flex-1">
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        completedModules === totalModules 
                          ? "bg-blue-100 text-blue-800" 
                          : "bg-blue-100 text-blue-800"
                      } self-start`}>
                        {completedModules === totalModules ? "Completed" : "In Progress"}
                      </span>
                      <span className="text-gray-700 font-medium text-sm sm:text-base">
                        {completedModules} of {totalModules} modules completed
                      </span>
                    </div>
                    <div className="w-full">
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div 
                          className="h-3 rounded-full transition-all duration-300 bg-blue-500"
                          style={{ width: `${progressPercentage}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">
                      {completedModules === totalModules 
                        ? "🎉 All modules completed! You're ready for certification."
                        : `${totalModules - completedModules} module${totalModules - completedModules !== 1 ? 's' : ''} remaining`
                      }
                    </div>
                  </div>
                </div>

                {/* Right Side - Circular Progress */}
                <div className="text-center lg:ml-auto w-full lg:w-auto">
                  <div className="relative inline-block">
                    <svg className="w-24 h-24 lg:w-28 lg:h-28 transform -rotate-90" viewBox="0 0 100 100">
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        stroke="currentColor"
                        strokeWidth="8"
                        fill="transparent"
                        className="text-gray-200"
                      />
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        stroke="currentColor"
                        strokeWidth="8"
                        fill="transparent"
                        className="text-blue-500"
                        strokeDasharray={`${2 * Math.PI * 40}`}
                        strokeDashoffset={`${2 * Math.PI * 40 * (1 - progressPercentage / 100)}`}
                        strokeLinecap="round"
                        style={{
                          transformOrigin: 'center',
                          transform: 'rotate(-90deg)'
                        }}
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className= "text-2xl lg:text-3xl font-bold text-blue-500">
                        {progressPercentage}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Course Modules - Strip Cards */}
        <div className="space-y-4 px-3">
          {modules.map((module,i) => (
            <div 
              key={module.id} 
              className="bg-white rounded-lg shadow-md border border-gray-100 overflow-hidden hover:shadow-lg transition-all duration-200"
            >
              {/* Top Strip - Three Part Layout */}
              <div 
                className={`px-6 py-4 border-b ${getStatusBackgroundColor(module.status)} ${getStatusBorderColor(module.status)} ${module.status !== 'locked' ? 'cursor-pointer' : ''}`}
                onClick={module.status !== 'locked' ? () => toggleModuleExpansion(module._id) : undefined}
              >
                 <div className="flex items-center justify-between">
                   {/* Left Side - Module Number, Title, Status, and Duration */}
                   <div className="flex items-center gap-4">
                     {/* Module Number Icon */}
                     <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-semibold text-sm">
                       {i+1}
                     </div>
                     
                     {/* Module Title, Status, and Duration */}
                     <div className="flex flex-col gap-1">
                       <div className="flex items-center gap-2">
                         <h3 className="text-lg font-semibold text-gray-700">
                           {module.title}
                         </h3>
                         <span className={`px-2 py-1 rounded-full text-xs font-medium text-white ${getStatusColor(module.status)}`}>
                           {module.status.charAt(0).toUpperCase() + module.status.slice(1)}
                         </span>
                       </div>
                       <div className="flex items-center gap-2 text-sm text-gray-500">
                         <Clock className="w-4 h-4" />
                         <span>{module.duration}</span>
                       </div>
                     </div>
                   </div>

                   {/* Right Side - Status Icon, Actions, and Expand/Collapse */}
                   <div className="flex items-center gap-3">
                     {/* Status Icon */}
                     {getStatusIcon(module.status)}
                     
                     {/* Status-based Buttons */}
                     {module.status === "live" && (
                       <button
                         className="px-4 py-2 rounded-lg text-white font-medium transition-colors duration-200 bg-orange-500 hover:bg-orange-600 flex items-center gap-2"
                         onClick={(e) => {
                           e.stopPropagation();
                           handleStartQuiz(module._id);
                         }}
                       >
                         Start Test
                       </button>
                     )}
                     
                     {module.status === "available" && (
                       <button
                         className="px-4 py-2 rounded-lg text-white font-medium transition-colors duration-200 bg-blue-500 hover:bg-blue-600 flex items-center gap-2"
                         onClick={(e) => {
                           e.stopPropagation();
                           handleModuleClick(module._id);
                         }}
                       >
                         Start Module
                       </button>
                     )}

                     {/* Expand/Collapse Indicator */}
                     <div className={`p-2 rounded-lg transition-colors duration-200 ${
                       module.status === 'locked' 
                         ? 'text-gray-400' 
                         : 'text-gray-600'
                     }`}>
                       {expandedModules.has(module._id) ? (
                         <ChevronUp className="w-5 h-5" />
                       ) : (
                         <ChevronDown className="w-5 h-5" />
                       )}
                     </div>
                   </div>
                 </div>
              </div>

              {/* Expandable Content */}
              {expandedModules.has(module._id) && (
                <div className="p-6">
                  <div className="w-full flex items-center justify-end gap-2 text-gray-600">
                    <Clock className="w-3 h-3" />
                    <span className="text-xs">{module.duration}</span>
                  </div>

                  <div className='w-full mb-4' dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(module.content) }} />
                  
                  {/* Collapse Circle Button */}
                  <div className="flex justify-center">
                    <button
                      onClick={() => toggleModuleExpansion(module._id)}
                      className="w-12 h-12 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors duration-200 flex items-center justify-center"
                    >
                      <ChevronUp className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Certificate Section */}
      {completedModules === totalModules && (
      <div className="text-center mt-12 py-8 bg-gray-50">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-200 rounded-full mb-6">
          <img src="/images/SVG (24).svg" alt="ADHD Certification" className="w-8 h-8" />
        </div>
        
        <h1 className="text-2xl font-semibold text-gray-900">
          Congratulations! You've Earned Your Certificate
        </h1>
        
        <p className="text-gray-600 mt-2 max-w-md mx-auto text-sm">
          You have successfully completed all modules of the ADHD Care Certification program.
        </p>

        <DownloadNurseCertifcate/>
      </div>
        )}

    </div>
    </>
  );

}

export default ADHDUserCourse;